import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ApiError,
  type ListValidationRulesResult,
  type SaveValidationRuleArgs,
} from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  Field,
  InlineAction,
  Panel,
  RuleBuilder,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { isFieldType } from '@whinerp/contracts/fields';
import type { RuleNode, RuleSchema } from '@whinerp/contracts/rules';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';
import { ruleBuilderLabels } from '../catalog/rule-labels.js';

/**
 * Validation rules, per object (F-091).
 *
 * The rule's name is the first thing asked for and the first thing listed,
 * because it is the thing the person a rule stops is shown - and the list says
 * in words which rules refuse and which only warn, since the two look
 * identical as rows and behave nothing alike.
 */

type Rule = ListValidationRulesResult['rules'][number];

interface Draft {
  ruleId: string | null;
  name: string;
  fieldKey: string;
  severity: 'warn' | 'block';
  message: string;
  scope: 'everyone' | 'roles';
  roleIds: string[];
  conditions: RuleNode;
}

const blankDraft = (fieldKey: string): Draft => ({
  ruleId: null,
  name: '',
  fieldKey,
  severity: 'block',
  message: '',
  scope: 'everyone',
  roleIds: [],
  conditions: { all: [] },
});

export function ValidationRulesPanel({
  objects,
  roles,
}: {
  objects: Array<{ key: string; pluralLabel: string }>;
  roles: Array<{ roleId: string; name: string }>;
}): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const entitlement = useEntitlement('F-091');
  const [objectKey, setObjectKey] = useState(objects[0]?.key ?? 'contact');
  const [draft, setDraft] = useState<Draft | null>(null);

  const rules = useQuery<ListValidationRulesResult, ApiError>({
    queryKey: ['validation-rules', objectKey],
    queryFn: () => api.listValidationRules({ path: { objectKey } }),
    enabled: entitlement.state === 'on',
  });

  const save = useMutation<unknown, ApiError, Draft>({
    mutationFn: (current) =>
      api.saveValidationRule({
        path: { objectKey },
        body: {
          ruleId: current.ruleId,
          name: current.name,
          fieldKey: current.fieldKey,
          severity: current.severity,
          message: current.message,
          scope: current.scope,
          roleIds: current.scope === 'roles' ? current.roleIds : [],
          conditions: current.conditions as SaveValidationRuleArgs['body']['conditions'],
        },
      }),
    onSuccess: async () => {
      setDraft(null);
      await queryClient.invalidateQueries({ queryKey: ['validation-rules', objectKey] });
    },
  });

  const remove = useMutation<unknown, ApiError, string>({
    mutationFn: (ruleId) => api.deleteValidationRule({ path: { ruleId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['validation-rules', objectKey] });
    },
  });

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="validation-rules" id="validation-rules">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('validation.title')}</SectionLabel>
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('validation.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('validation.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (rules.isPending) return <Skeleton className="h-block-md w-full" />;
  if (rules.isError) {
    return (
      <ErrorState title={t('state.error')} body={errorMessage(rules.error)} onRetry={() => void rules.refetch()} />
    );
  }

  const schema = toRuleSchema(rules.data.schema);
  const fieldLabel = (key: string): string => schema.fields.find((field) => field.key === key)?.label ?? key;

  return (
    <Panel data-region="validation-rules" id="validation-rules">
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <SectionLabel>{t('validation.title')}</SectionLabel>
          <label className="ms-auto flex items-center gap-2 text-dense text-ink-600">
            {t('validation.object')}
            <select
              data-validation-object
              value={objectKey}
              onChange={(event) => {
                setObjectKey(event.currentTarget.value);
                setDraft(null);
              }}
              className="h-input rounded-control border border-control bg-surface px-2 text-dense text-ink"
            >
              {objects.map((object) => (
                <option key={object.key} value={object.key}>
                  {object.pluralLabel}
                </option>
              ))}
            </select>
          </label>
          <Button
            intent="secondary"
            size="small"
            icon="Plus"
            data-new-validation-rule
            onClick={() => setDraft(blankDraft(schema.fields[0]?.key ?? ''))}
          >
            {t('validation.new')}
          </Button>
        </div>
        <p className="text-meta text-ink-meta">{t('validation.body')}</p>

        {rules.data.rules.length === 0 ? (
          <p data-validation-empty className="text-dense text-ink-meta">
            {t('validation.empty')}
          </p>
        ) : (
          <ul className="flex flex-col">
            {rules.data.rules.map((rule) => (
              <RuleRow
                key={rule.ruleId}
                rule={rule}
                fieldLabel={fieldLabel(rule.fieldKey)}
                onEdit={() => setDraft(fromRule(rule))}
                onRemove={() => remove.mutate(rule.ruleId)}
              />
            ))}
          </ul>
        )}
        {remove.isError ? (
          <p data-validation-refusal className="text-dense text-danger-text">
            {errorMessage(remove.error)}
          </p>
        ) : null}

        {draft === null ? null : (
          <div data-validation-editor className="flex flex-col gap-3 rounded-panel border border-rule p-3">
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <Field
                label={t('validation.name')}
                data-validation-name
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.currentTarget.value })}
              />
              <label className="flex flex-col gap-1">
                <span className="text-micro uppercase tracking-label text-ink-label">{t('validation.field')}</span>
                <select
                  data-validation-field
                  value={draft.fieldKey}
                  onChange={(event) => setDraft({ ...draft, fieldKey: event.currentTarget.value })}
                  className="h-input rounded-control border border-control bg-surface px-2 text-body text-ink"
                >
                  {schema.fields.map((field) => (
                    <option key={field.key} value={field.key}>
                      {field.label}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <fieldset className="flex flex-wrap items-center gap-4">
              <legend className="text-micro uppercase tracking-label text-ink-label">{t('validation.severity')}</legend>
              {(['block', 'warn'] as const).map((severity) => (
                <label key={severity} className="flex items-center gap-2 text-dense text-ink">
                  <input
                    type="radio"
                    name="validation-severity"
                    data-validation-severity={severity}
                    checked={draft.severity === severity}
                    onChange={() => setDraft({ ...draft, severity })}
                  />
                  {t(severity === 'block' ? 'validation.severity.block' : 'validation.severity.warn')}
                </label>
              ))}
            </fieldset>

            <Field
              label={t('validation.message')}
              data-validation-message
              value={draft.message}
              onChange={(event) => setDraft({ ...draft, message: event.currentTarget.value })}
            />

            <fieldset className="flex flex-col gap-2">
              <legend className="text-micro uppercase tracking-label text-ink-label">{t('validation.scope')}</legend>
              <div className="flex flex-wrap items-center gap-4">
                {(['everyone', 'roles'] as const).map((scope) => (
                  <label key={scope} className="flex items-center gap-2 text-dense text-ink">
                    <input
                      type="radio"
                      name="validation-scope"
                      data-validation-scope={scope}
                      checked={draft.scope === scope}
                      onChange={() => setDraft({ ...draft, scope })}
                    />
                    {t(scope === 'everyone' ? 'validation.scope.everyone' : 'validation.scope.roles')}
                  </label>
                ))}
              </div>
              {draft.scope === 'roles' ? (
                <div className="flex flex-wrap gap-3">
                  {roles.map((role) => (
                    <label key={role.roleId} className="flex items-center gap-2 text-dense text-ink">
                      <input
                        type="checkbox"
                        data-validation-role={role.roleId}
                        checked={draft.roleIds.includes(role.roleId)}
                        onChange={(event) =>
                          setDraft({
                            ...draft,
                            roleIds: event.currentTarget.checked
                              ? [...draft.roleIds, role.roleId]
                              : draft.roleIds.filter((id) => id !== role.roleId),
                          })
                        }
                      />
                      {role.name}
                    </label>
                  ))}
                </div>
              ) : null}
            </fieldset>

            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-baseline gap-2">
                <span className="text-micro uppercase tracking-label text-ink-label">
                  {t('validation.conditions')}
                </span>
                {/* Required-when is a rule like any other: the chosen field has
                    no value. One press writes that condition, so "required"
                    is not a second mechanism with its own semantics. */}
                <InlineAction
                  data-validation-make-required
                  onClick={() =>
                    setDraft({
                      ...draft,
                      conditions: { all: [{ field: draft.fieldKey, operator: 'is-not-set' }] },
                    })
                  }
                >
                  {t('validation.makeRequired')}
                </InlineAction>
              </div>
              <RuleBuilder
                data-validation-conditions
                schema={schema}
                value={draft.conditions}
                onChange={(next) => setDraft({ ...draft, conditions: next })}
                labels={ruleBuilderLabels(t)}
              />
            </div>

            {save.isError ? (
              <div data-validation-refusal className="flex flex-col gap-1 text-dense text-danger-text">
                <p>{errorMessage(save.error)}</p>
                {(save.error.problems ?? []).map((problem) => (
                  <p key={`${problem.field}:${problem.message}`} className="text-meta">
                    {problem.message}
                  </p>
                ))}
              </div>
            ) : null}

            <div className="flex gap-2">
              <Button
                intent="primary"
                size="small"
                data-save-validation-rule
                busy={save.isPending}
                onClick={() => save.mutate(draft)}
              >
                {t('validation.save')}
              </Button>
              <Button intent="ghost" size="small" onClick={() => setDraft(null)}>
                {t('validation.cancel')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
}

function RuleRow({
  rule,
  fieldLabel,
  onEdit,
  onRemove,
}: {
  rule: Rule;
  fieldLabel: string;
  onEdit: () => void;
  onRemove: () => void;
}): JSX.Element {
  const t = useT();
  // A rule for chosen roles whose roles were all deleted applies to nobody.
  // Said, because it looks like a working rule and stops no one.
  const appliesTo =
    rule.scope === 'everyone'
      ? t('validation.scope.everyone')
      : rule.roles.length === 0
        ? t('validation.scope.nobody')
        : rule.roles.map((role) => role.name).join(', ');
  return (
    <li
      data-validation-rule={rule.ruleId}
      className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t border-t-rule py-2 first:border-t-0"
    >
      <span className="text-body text-ink">{rule.name}</span>
      <span
        data-validation-severity-chip={rule.severity}
        className={
          rule.severity === 'block'
            ? 'rounded-chip bg-danger-tint px-1.5 text-meta text-danger-text'
            : 'rounded-chip bg-warning-tint px-1.5 text-meta text-warning-text'
        }
      >
        {t(rule.severity === 'block' ? 'validation.chip.block' : 'validation.chip.warn')}
      </span>
      {rule.active ? null : (
        <span className="rounded-chip bg-sunken px-1.5 text-meta text-ink-600">{t('validation.off')}</span>
      )}
      <span className="text-meta text-ink-meta">{fieldLabel}</span>
      <span data-validation-applies className="text-meta text-ink-meta">
        {appliesTo}
      </span>
      <span className="ms-auto flex gap-2">
        <InlineAction data-edit-validation-rule={rule.ruleId} onClick={onEdit}>
          {t('validation.edit')}
        </InlineAction>
        <InlineAction data-remove-validation-rule={rule.ruleId} onClick={onRemove}>
          {t('validation.delete')}
        </InlineAction>
      </span>
    </li>
  );
}

function fromRule(rule: Rule): Draft {
  return {
    ruleId: rule.ruleId,
    name: rule.name,
    fieldKey: rule.fieldKey,
    severity: rule.severity,
    message: rule.message,
    scope: rule.scope,
    roleIds: rule.roles.map((role) => role.roleId),
    conditions: rule.conditions as RuleNode,
  };
}

/** The published schema as the builder's type; a type the server sends that is not a field type is text. */
function toRuleSchema(published: ListValidationRulesResult['schema']): RuleSchema {
  return {
    key: published.key,
    label: published.label,
    outcome: published.outcome,
    maxDepth: published.maxDepth,
    emptyMeans: published.emptyMeans,
    fields: published.fields.map((field) => ({
      key: field.key,
      label: field.label,
      type: isFieldType(field.type) ? field.type : 'text',
      ...(field.options ? { options: field.options } : {}),
    })),
  };
}
